import { useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Check, Lock } from "lucide-react";
import { Outlet, useParams } from "react-router";
import type { StageGateResponse } from "@patent-studio/api-types";
import { buttonChrome } from "@/components/ui/button";
import { IconTile } from "@/components/ui/IconTile";
import { MicroLabel } from "@/components/ui/MicroLabel";
import { Panel } from "@/components/ui/Panel";
import { Spinner } from "@/components/ui/Spinner";
import { thesisGateScopes } from "@/hooks/query-keys";
import { useStageGate } from "@/hooks/use-stage-gate";
import { MaybeLink } from "../layout/MaybeLink";
import { StageGuide } from "../layout/StagePage";
import {
  CRITERION_ROWS,
  GATED_STAGES,
  type GatedStageId,
} from "./gated-stages";
import { ReadProblem } from "@/components/patterns/ReadProblem";

/** One criterion: the tick the server decided, what it is, how many meet it and where, and the way there. */
function CriterionRow({
  taskId,
  row,
}: {
  taskId: string;
  row: StageGateResponse["criteria"][number];
}) {
  const shown = CRITERION_ROWS[row.criterion];
  return (
    <li className="flex items-center gap-3 border-b border-border py-3">
      <IconTile tone={row.met ? "done" : "open"} shape="round" size="xs">
        <Check strokeWidth={3.2} />
      </IconTile>
      <span className="sr-only">{row.met ? "Confirmed" : "Not yet"}</span>
      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
        <b className="text-[13.5px] text-foreground">{shown.label}</b>
        <span className="text-xs text-muted-foreground">
          {row.met ? `${row.count} confirmed` : "None yet"} · {shown.where}
        </span>
      </div>
      <MaybeLink
        to={shown.to(taskId)}
        className={buttonChrome({ variant: "way" })}
      >
        Go to {shown.tab}
      </MaybeLink>
    </li>
  );
}

/**
 * What a gated stage shows while it is shut, as v0.28 draws it: the stage's
 * walkthrough, then one card naming the stage, what opens it, and each
 * criterion as the server read it.
 */
function LockedStage({
  taskId,
  stage,
  gate,
}: {
  taskId: string;
  stage: GatedStageId;
  gate: StageGateResponse;
}) {
  const { name, guide } = GATED_STAGES[stage];
  return (
    <section
      data-testid="locked-stage"
      className="mx-auto w-full max-w-[640px] pb-10 pt-6"
    >
      <div className="flex justify-end">
        <StageGuide guide={guide} />
      </div>
      {/* The card stands a gutter inside the column the walkthrough sits at the edge of. */}
      <Panel pad="ample" className="mx-6 flex flex-col gap-4">
        <IconTile size="md">
          <Lock />
        </IconTile>
        <div className="flex flex-col gap-1.5">
          <MicroLabel as="p">{name} is locked</MicroLabel>
          {/* A heading inside the card, not the page's title: StagePage alone titles a page. */}
          <h2 className="text-xl font-bold leading-[1.35] text-primary">
            To unlock this section, confirm your draft&apos;s elements,
            positions and novelty.
          </h2>
        </div>
        <ul className="flex flex-col border-t border-border">
          {gate.criteria.map((row) => (
            <CriterionRow key={row.criterion} taskId={taskId} row={row} />
          ))}
        </ul>
        <p className="text-xs leading-normal text-muted-foreground">
          This section opens as soon as all three are confirmed, and stays open
          afterwards.
        </p>
      </Panel>
    </section>
  );
}

/**
 * Whether a stage this page showed locked may render now that the gate is open.
 *
 * What opened it may have been written in another tab, and then every read
 * the stage starts from was taken while it was locked: Claim Strategy's own
 * guard would act on a set that no longer stands. Those reads are asked again
 * first, and the stage renders once they answer. A stage open at its first
 * read renders at once.
 */
function useFreshOnOpening(taskId: string, open: boolean | undefined): boolean {
  const queryClient = useQueryClient();
  const [shownLocked, setShownLocked] = useState(false);
  const [fresh, setFresh] = useState(false);
  if (open === false && !shownLocked) setShownLocked(true);
  const opening = open === true && shownLocked && !fresh;
  useEffect(() => {
    if (!opening) return;
    let live = true;
    void Promise.all(
      thesisGateScopes(taskId).map((queryKey) =>
        queryClient.invalidateQueries({ queryKey }),
      ),
    ).then(() => {
      if (live) setFresh(true);
    });
    return () => {
      live = false;
    };
  }, [opening, taskId, queryClient]);
  return !opening;
}

/**
 * What an open stage says while a criterion that opened it has dropped since,
 * as v0.28 words it, naming each one in the locked page's order. The stage
 * stays open under it (decision 4); the banner goes when the criterion is met
 * again.
 */
function DropBanner({ gate }: { gate: StageGateResponse }) {
  const dropped = new Set(gate.open_drops.map((drop) => drop.criterion));
  const named = gate.criteria
    .filter((row) => dropped.has(row.criterion))
    .map((row) => CRITERION_ROWS[row.criterion].noun);
  if (named.length === 0) return null;
  return (
    <Panel
      role="status"
      tone="warning"
      className="mx-auto mt-6 flex w-full max-w-[1000px] items-start gap-2.5 text-detail leading-normal"
    >
      <span
        aria-hidden
        className="mt-1.5 size-2 flex-none rounded-full bg-current"
      />
      <p>
        <b>
          Your Thesis no longer has a confirmed {named.join(", a confirmed ")}.
        </b>{" "}
        This section stays open, but Patent Studio has less to check your work
        against and may ask questions it would not otherwise need.
      </p>
    </Panel>
  );
}

function Gate({ taskId, stage }: { taskId: string; stage: GatedStageId }) {
  const gate = useStageGate(taskId);
  const { data } = gate;
  const settled = useFreshOnOpening(taskId, data?.open);
  // A failed read again keeps the gate it had, so the stage and the words in it stay mounted.
  const problem = (
    <ReadProblem
      query={gate}
      what="whether this stage is open"
      className="mt-6"
    />
  );
  if (data === undefined && gate.isError) return problem;
  if (data === undefined || !settled) {
    return (
      <div className="grid place-items-center pt-16">
        <Spinner />
      </div>
    );
  }
  // The stage mounts only once the gate is open, so its own reads never start behind the lock.
  return data.open ? (
    <>
      {problem}
      {GATED_STAGES[stage].dropBanner && <DropBanner gate={data} />}
      <Outlet />
    </>
  ) : (
    <>
      {problem}
      <LockedStage taskId={taskId} stage={stage} gate={data} />
    </>
  );
}

/**
 * The way onto a gated stage, at the stage's own address. While the server
 * says the stage is shut, the locked page stands in its place; the moment it
 * says open, the stage the attorney asked for renders where they are, with no
 * move and no reload. Reads its own route params.
 */
export function StageGateGuard({ stage }: { stage: GatedStageId }) {
  const { taskId } = useParams<{ taskId: string }>();
  return taskId === undefined ? (
    <Outlet />
  ) : (
    <Gate taskId={taskId} stage={stage} />
  );
}
